import { Theme, useCss } from 'bold-ui'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { useSession } from 'components/auth/useSession'
import { Breadcrumb } from 'components/breadcrumb'
import { parseDateFromLocalStorage } from 'components/form'
import { PageLoading } from 'components/loading'
import { PecSwitch, PrivateRoute } from 'components/route'
import { useFlags } from 'config/useFlagsContext'
import {
  useAcompanhamentoPreNatalCardQuery,
  useAgendamentosDiaQuery,
  useAtendimentoIndividualRetificacaoViewQuery,
  useAtendimentoIndividualViewQuery,
  useAtendimentoObservacaoViewQuery,
  useCiapCidAtendimentoQuery,
  useIvcfEmAtendimentoObservacaoQuery,
  useLoadAntecedentesQuery,
  useProblemasModalSimplificadoQuery,
  useProcedimentosAutomaticosQuery,
  useRegistroAvaliacaoPuericulturaQuery,
} from 'graphql/hooks.generated'
import {
  AtendimentoProfissional,
  CondutaEnum,
  SituacaoAgendadoEnum,
  SituacaoProblema,
  TipoAtendimentoEnum,
  TipoAtendimentoProfissional,
  TipoEstabelecimentoEnum,
} from 'graphql/types.generated'
import { AtendimentoContextInputModel } from 'hooks/atendimento-context/model-atendimentoContext'
import { AtendimentoProvider } from 'hooks/atendimento-context/useAtendimentoContext'
import { useAlertasSdc } from 'hooks/sdc/useAlertasSdc'
import { useLocalStorageState } from 'hooks/useStorage'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Redirect, Route, useRouteMatch } from 'react-router'
import { tipoAtendimentoToTipoServicoMap } from 'types/enums'
import { partition } from 'util/array'
import { isUndefinedOrNull } from 'util/checks'
import { isCidadaoIdoso } from 'util/isCidadaoIdoso'
import { isEmpty } from 'util/validation/Util'
import { grupoCboVisualizarAcompanhamentos } from 'view/cidadao/authorization/acessos'
import { useRetificacaoStorage } from 'view/retificacao-atendimento/useJustificativaRetificacaoStorage'

import Permissions from '../../../types/Permissions'
import { initialValueRetificacao } from '../../retificacao-atendimento/util-retificacaoAtendimento'
import { AtendimentoVacinacaoState, initialValue as initialValueVacinacao } from '../atendimento-vacinacao/model'
import { grupoCboHistorico } from '../detail/acessos'
import { ACOMPANHAMENTOS_URL, AcompanhamentosView } from '../detail/acompanhamentos/AcompanhamentosView'
import { useAcompanhamentoPuerperio } from '../detail/acompanhamentos/puerperio/hooks/useAcompanhamentoPuerperio'
import { AgendamentosCidadaoAtendimentoView } from '../detail/agendamentos/AgendamentosCidadaoAtendimentoView'
import { atendimentoName } from '../detail/atendimentoName'
import { CidadaoDetailView } from '../detail/cadastro-cidadao/CidadaoDetailView'
import { FolhaRostoView } from '../detail/folha-rosto/FolhaRostoView'
import { AtendimentoHeader } from '../detail/header/AtendimentoHeader'
import { HistoricoAtendimentoView } from '../detail/historico/HistoricoAtendimentoView'
import { ProntuarioFamiliarView } from '../detail/prontuario-familiar/ProntuarioFamiliarView'
import { SistemasExternosAtendimentoView } from '../detail/sistemas-externos/SistemasExternosAtendimentoView'
import { AntecedentesFormModel } from '../detail/soap/antecedentes/AntecedentesForm'
import { initialValueAntecedentes } from '../detail/soap/antecedentes/model-antecedentes'
import { usePeriodoGestacional } from '../detail/soap/aside/pre-natal/hooks/usePeriodoGestacional'
import { getAltoRisco } from '../detail/soap/aside/pre-natal/util/util-acompanhamentoPreNatal'
import { AgendarConsultaSectionModel } from '../detail/soap/finalizacao/components/agendar-consulta/model-agendarConsultaAtendimento'
import { TipoCompartilhamentoCuidadoPlanoEnum } from '../detail/soap/plano/cuidado-compartilhado/model-cuidadocompartilhado'
import { procedimentosUnicosOdontologia } from '../detail/soap/plano/evolucoes-odontologicas/util-EvolucoesOdontologicasPlano'
import { CIAP_W78_CODIGO, CID_Z34_CODIGO } from '../detail/soap/pre-natal/model-preNatal'
import { SoapView } from '../detail/soap/SoapView'
import { VacinacaoReadOnlyView, VacinacaoView } from '../detail/vacinacao/VacinacaoView'
import { calculateProcedimentoAutomaticoToAdd } from '../utils/util-procedimentoAutomatico'
import { extractObservacaoEditableModules, initialValueObservacao } from './atendimento-observacao/util-atendObservacao'
import { ConfirmarNaoAnfitriaoAtenderModal } from './ConfirmarNaoAnfitriaoAtenderModal'
import { SoapState, TIPOS_ATENDIMENTO_CONCLUEM_AGENDAMENTO, TipoServicoModel } from './model'
import {
  getCiapCidPreNatal,
  getCidadaoAtendimento,
  getTiposAtendimento,
  initialValue,
} from './util-atendimentoIndividual'

interface AtendimentoIndividualViewProps {
  atendimentoProfissionalId: ID
  isRetificacao?: boolean
}

export function AtendimentoIndividualView(props: Readonly<AtendimentoIndividualViewProps>) {
  const { atendimentoProfissionalId, isRetificacao = false } = props
  const { data: sessao, hasAuthorization, hasCboAuth, isEstagio, tipoEstabelecimento } = useSession()
  const { acesso } = useAcessoLotacaoOrEstagio()
  const match = useRouteMatch()
  const today = moment().startOf('day').toDate()
  const cbo2002 = acesso?.cbo.cbo2002
  const unidadeSaude = acesso?.unidadeSaude
  const {
    PILOTO_VINCULACAO_CUIDADO_ENABLED,
    PILOTO_PRONTUARIO_FAMILIAR_ENABLED,
    PILOTO_PARTICIPACAO_OBRIGATORIA_TELECONSULTA_ENABLED,
  } = useFlags()

  const [cacheState, setCacheState, deleteCacheState] = useLocalStorageState<SoapState>(
    `${sessao.acesso.id}/atendimento/individual/${atendimentoProfissionalId}`,
    { checkVersionCompatibility: true, modifier: cacheParser }
  )

  const { deleteCache } = useAlertasSdc()

  const handleDeleteCacheSoap = useCallback(
    (updateState?: boolean) => {
      deleteCache()
      deleteCacheState(updateState)
    },
    [deleteCache, deleteCacheState]
  )

  const [initialStateLoaded, setInitialStateLoaded] = useState(!!cacheState && !isRetificacao)
  // Se este recurso for ser usado fora da retificação, ficar atento a erros deste tipo: https://github.com/laboratoriobridge/pec/issues/27082
  const [valoresModificadosRascunhoRetificacao, setValoresModificadosRascunhoRetificacao] = useState<SoapState>(null)
  const headerRef = useRef()

  const {
    data: { atendimentoIndividual: atendimentoProfissional },
    loading: loadingAtendimento,
  } = useAtendimentoIndividualViewQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      atendimentoProfissionalId,
    },
  })

  const {
    tipo: tipoAtendimentoProfissional,
    iniciadoEm: atendimentoProfissionalIniciadoEm,
    rascunho,
    lotacao,
    tipoAtendimento,
    atendimento: {
      id: atendimentoId = null,
      agendado = null,
      tiposServico = null,
      prontuario = null,
      isRegistroTardio = null,
      cidadao = null,
      statusRevisaoAtendimento = null,
    } = {},
  } = atendimentoProfissional || {}
  const prontuarioId = prontuario?.id
  const cidadaoCpfCns: string | undefined = cidadao?.cpf ?? cidadao?.cns

  const isProfissionalAnfitriaoAtendendo = agendado?.agendamentoPrincipal?.lotacaoAgendada.id === acesso.id
  const lotacaoProfissionalParticipante = isProfissionalAnfitriaoAtendendo ? agendado?.lotacaoParticipante : null

  const { getRetificacaoStorageValue } = useRetificacaoStorage(atendimentoId)

  const {
    isAtendimentoOdonto,
    showAsAtendimentoProcedimentos,
    isAtendimentoAgendado,
    observacao: {
      isAtendimentoObservacao,
      isObservacaoAndResponsavel,
      isObservacaoAndAuxiliar,
      isObservacaoEmAndamento,
      ultimoResponsavelObservacao,
      responsaveisObservacao,
      atendeuComoResponsavel,
    },
  } = getTiposAtendimento({
    atendimentoProfissional: {
      tipo: tipoAtendimentoProfissional,
      lotacao,
      estagio: atendimentoProfissional?.estagio,
      atendimentoProfissionalObservacao: atendimentoProfissional?.atendimentoProfissionalObservacao,
      finalizadoEm: atendimentoProfissional?.finalizadoEm,
    },
    isAtendimentoAgendado: !!agendado,
    statusRevisaoAtendimento: statusRevisaoAtendimento,
    isAcessoEstagio: isEstagio,
  })

  const {
    data: atendimentoObservacaoData,
    loading: loadingAtendimentoObservacaoQuery,
  } = useAtendimentoObservacaoViewQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isAtendimentoObservacao,
    variables: {
      atendimentoId,
    },
  })

  const {
    data: atendimentoIndividualRetificacao,
    loading: atendimentoIndividualRetificacaoQuery,
  } = useAtendimentoIndividualRetificacaoViewQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isRetificacao,
    variables: {
      atendimentoProfissionalId,
    },
  })

  const {
    data: { procedimentosAutomaticos },
    loading: loadingProcedimentosAutomaticos,
  } = useProcedimentosAutomaticosQuery({
    fetchPolicy: 'cache-first',
  })

  const {
    data: { registroAvaliacaoPuericultura },
    loading: loadingRegistroAvaliacaoPuericultura,
  } = useRegistroAvaliacaoPuericulturaQuery({
    fetchPolicy: 'cache-first',
  })

  const {
    data: { antecedente },
    loading: loadingAntecedente,
  } = useLoadAntecedentesQuery({
    variables: {
      id: prontuarioId,
    },
    skip: !prontuarioId,
  })

  const {
    data: { ciaps, cids },
    loading: loadingCiapCid,
  } = useCiapCidAtendimentoQuery({
    fetchPolicy: 'cache-first',
    variables: {
      inputCiap: {
        query: CIAP_W78_CODIGO,
      },
      inputCid10: {
        query: CID_Z34_CODIGO,
      },
    },
  })

  const {
    loading: loadingProblemasCidadao,
    data: { problemas },
  } = useProblemasModalSimplificadoQuery({
    variables: { filtro: { prontuarioId } },
    skip: !prontuarioId,
  })

  const { loading: loadingAcompanhamentoPreNatalCard, data: dataPreNatalCard } = useAcompanhamentoPreNatalCardQuery({
    variables: { input: prontuarioId },
    skip: !prontuarioId,
  })

  const preNatalAtivo = prontuario?.preNatalAtivo
  const ultimaDumPreNatalAtivo = preNatalAtivo?.ultimaDum

  const { loading: loadingDataPeriodoGestacional, idadeGestacional } = usePeriodoGestacional({
    prontuarioId: prontuarioId,
    dum: ultimaDumPreNatalAtivo,
    dataAtendimento: atendimentoProfissionalIniciadoEm,
  })

  const { data: agendamentosDiaQueryResults, loading: loadingAgendamento } = useAgendamentosDiaQuery({
    variables: {
      input: {
        cidadaoId: cidadao?.id,
        dataAgendadoInicio: today,
        dataAgendadoFim: today,
        situacao: [SituacaoAgendadoEnum.AGENDADO, SituacaoAgendadoEnum.CIDADAO_PRESENTE_NA_UNIDADE],
        unidadeSaudeId: unidadeSaude.id,
        isForaDaUbs: false,
        isOrigemAtencaoDomiciliar: false,
      },
    },
    skip: !cidadao?.id,
  })

  const { data: informacoesPuerperio } = useAcompanhamentoPuerperio(prontuarioId, atendimentoProfissionalIniciadoEm)

  const atendimentosProfUltimaGestacao = dataPreNatalCard?.atendimentosProfUltimaGestacao
  const isGestante = !isEmpty(prontuario?.preNatalAtivo)
  const isPuerpera = informacoesPuerperio?.puerperioAtivo
  const acompanhamentoPuerperioAtivo = informacoesPuerperio?.acompanhamentoAtivo

  const hasAccessToAcompanhamentos =
    hasCboAuth(grupoCboVisualizarAcompanhamentos) && hasAuthorization(Permissions.visualizarProntuario)

  const hasToRenderAcompanhamentos = hasAccessToAcompanhamentos && (isGestante || acompanhamentoPuerperioAtivo)

  const isPreNatalAltoRisco = !loadingAcompanhamentoPreNatalCard && getAltoRisco(atendimentosProfUltimaGestacao)

  const ciapCidPreNatal = useMemo(() => getCiapCidPreNatal(ciaps?.content, cids?.content), [ciaps, cids])

  const cidadaoAtendimento = useMemo(
    () => getCidadaoAtendimento(atendimentoProfissionalIniciadoEm, cidadao, isGestante, atendimentosProfUltimaGestacao),
    [atendimentoProfissionalIniciadoEm, cidadao, isGestante, atendimentosProfUltimaGestacao]
  )
  const acessoIvcf = isCidadaoIdoso(cidadaoAtendimento.idadeEmAnos)

  const { data: ivcfObservacaoData, loading: isLoadingIvcfObservacaoData } = useIvcfEmAtendimentoObservacaoQuery({
    variables: { id: atendimentoProfissionalId },
    skip: (!acessoIvcf && !atendimentoProfissionalId) || !isAtendimentoObservacao,
  })

  const hasIvcfPreenchidoEmAtendObservacaoAnterior = useMemo(
    () => isObservacaoAndResponsavel && !isEmpty(ivcfObservacaoData?.atendimentoIndividual?.ivcf?.id),
    [isObservacaoAndResponsavel, ivcfObservacaoData]
  )

  const nomeAtendimento = atendimentoName(tipoAtendimentoProfissional, isObservacaoAndAuxiliar)
  const agendamentosDia = agendamentosDiaQueryResults?.agendados?.content

  const isLoading =
    loadingAtendimento ||
    loadingAgendamento ||
    loadingAntecedente ||
    loadingProcedimentosAutomaticos ||
    loadingRegistroAvaliacaoPuericultura ||
    loadingCiapCid ||
    loadingProblemasCidadao ||
    loadingAcompanhamentoPreNatalCard ||
    loadingAtendimentoObservacaoQuery ||
    loadingDataPeriodoGestacional ||
    atendimentoIndividualRetificacaoQuery ||
    isLoadingIvcfObservacaoData

  const hasEncaminhamentoExterno =
    !isEmpty(cacheState?.plano?.encaminhamentoExterno?.encaminhamentosEspecializadosRecentes) ||
    !isEmpty(cacheState?.plano?.encaminhamentoExterno?.encaminhamentosRecentes) ||
    !isEmpty(
      cacheState?.plano?.cuidadosCompartilhado?.find(
        (item) => item.tipo === TipoCompartilhamentoCuidadoPlanoEnum.GUIA_ENCAMINHAMENTO
      )
    )

  const qtdTotalProcedimentos =
    (cacheState?.plano?.intervencoesProcedimentos?.procedimentosPlano?.procedimentos?.length ?? 0) +
    (cacheState?.finalizacao?.procedimentosAdministrativos?.length ?? 0) +
    (procedimentosUnicosOdontologia(cacheState?.plano?.odontologia).length ?? 0)

  const concluiAgendamento =
    agendado &&
    (TIPOS_ATENDIMENTO_CONCLUEM_AGENDAMENTO.includes(cacheState?.finalizacao?.tipoAtendimento) ||
      (showAsAtendimentoProcedimentos && agendado.lotacaoAgendada.id === acesso?.id))

  const hasAccessToCadastrarLotesImunobiologico = hasAuthorization(
    Permissions.cadastrarEditarEExcluirLotesDeImunobiologico
  )

  const shouldClearEncaminhamentosCache = useMemo(
    () => !isLoading && PILOTO_VINCULACAO_CUIDADO_ENABLED && !isAtendimentoOdonto,
    [PILOTO_VINCULACAO_CUIDADO_ENABLED, isAtendimentoOdonto, isLoading]
  )

  useEffect(() => {
    const canCreateNovoAtendimento = hasAuthorization(Permissions.visualizarListaDeAtendimento.cadastrarEditarEExcluir)

    if (!isLoading) {
      const agendadoSelecionado = cacheState?.finalizacao?.desfechoAtendimento?.atendimento?.agendado
      if (agendadoSelecionado && !agendamentosDia?.find((agendamentos) => agendamentos.id === agendadoSelecionado)) {
        cacheState.finalizacao.desfechoAtendimento.atendimento.agendado = undefined
      }

      if (!initialStateLoaded) {
        if (atendimentoProfissional?.rascunho) {
          setCacheState(cacheParser(JSON.parse(atendimentoProfissional.rascunho) as SoapState))
        } else if (isRetificacao) {
          setValoresModificadosRascunhoRetificacao(cacheState)
          setCacheState(
            initialValueRetificacao(
              atendimentoIndividualRetificacao?.atendimentoIndividual,
              getRetificacaoStorageValue(),
              cidadaoCpfCns
            )
          )
        } else {
          const tiposServicoFiltrado = filterTiposServico(tiposServico, tipoAtendimentoProfissional)

          const tipoAtendimentoInitialValue = getTipoAtendimentoInitialValue(
            tipoEstabelecimento,
            isObservacaoAndResponsavel
          )

          const initialProced = !isObservacaoEmAndamento
            ? calculateProcedimentoAutomaticoToAdd({
                tipoAtendimentoProfissional,
                tipoEstabelecimento,
                tipoAtendimento: tipoAtendimentoInitialValue,
                cbo2002,
                isProfResponsavelObservacao: isObservacaoAndResponsavel,
                tipoConsulta: null,
              })
            : null

          const procedimentoAdministrativo = procedimentosAutomaticos?.find((proced) => proced.codigo === initialProced)

          const atendimentoInitialValues = initialValue({
            procedimentoAdministrativo,
            tipoAtendimento: tipoAtendimentoInitialValue,
            agendamentosDia,
            tiposServicoFiltrado,
            canCreateNovoAtendimento,
            isAtendimentoAgendado,
            isAtendimentoProcedimentos: showAsAtendimentoProcedimentos,
            isObservacaoAndResponsavel,
            isObservacaoAndAuxiliar,
            isRegistroTardio,
            idadeGestacional,
            atendimentoCompartilhadoLotacao: lotacaoProfissionalParticipante,
            possuiVideochamada: !!agendado?.videochamada?.id,
            emailCidadao: cidadao.contato?.email,
            isParticipacaoTeleconsultaEnabled: PILOTO_PARTICIPACAO_OBRIGATORIA_TELECONSULTA_ENABLED,
          })

          const atendimentoObservacaoInitialValues =
            isAtendimentoObservacao &&
            initialValueObservacao(
              atendimentoInitialValues,
              atendimentoProfissionalIniciadoEm,
              tipoAtendimentoProfissional,
              hasIvcfPreenchidoEmAtendObservacaoAnterior,
              extractObservacaoEditableModules(
                atendimentoObservacaoData?.atendimento?.atendimentoProfissional,
                shouldClearEncaminhamentosCache
              ),
              cidadao?.contato?.email,
              atendimentoProfissional as AtendimentoProfissional
            )

          const vacinacaoInitialValues: AtendimentoVacinacaoState = initialValueVacinacao(
            prontuario?.puerpera,
            isGestante,
            tiposServico,
            canCreateNovoAtendimento,
            agendamentosDia
          )

          const antecedentesInitialValues: AntecedentesFormModel = initialValueAntecedentes(antecedente)

          setCacheState({
            ...atendimentoInitialValues,
            ...atendimentoObservacaoInitialValues,
            finalizacao: {
              ...atendimentoInitialValues.finalizacao,
              ...atendimentoObservacaoInitialValues.finalizacao,
              conduta:
                atendimentoObservacaoInitialValues.finalizacao?.conduta.filter(
                  (item) => item !== CondutaEnum.MANTER_EM_OBSERVACAO
                ) ?? [],
            },
            vacinacao: vacinacaoInitialValues,
            antecedentes: antecedentesInitialValues,
          })
        }
      }
      setInitialStateLoaded(true)
    }
  }, [
    agendamentosDia,
    atendimentoProfissional,
    cacheState,
    initialStateLoaded,
    isLoading,
    procedimentosAutomaticos,
    prontuario,
    sessao.acesso.id,
    setCacheState,
    tiposServico,
    antecedente,
    isGestante,
    isAtendimentoOdonto,
    isObservacaoAndResponsavel,
    isAtendimentoObservacao,
    isObservacaoAndAuxiliar,
    isAtendimentoAgendado,
    isRegistroTardio,
    hasAuthorization,
    tipoAtendimentoProfissional,
    showAsAtendimentoProcedimentos,
    tipoAtendimento,
    atendimentoObservacaoData,
    tipoEstabelecimento,
    cbo2002,
    atendimentoProfissionalIniciadoEm,
    isObservacaoEmAndamento,
    lotacaoProfissionalParticipante,
    agendado,
    shouldClearEncaminhamentosCache,
    isRetificacao,
    atendimentoIndividualRetificacao,
    hasIvcfPreenchidoEmAtendObservacaoAnterior,
    cidadao,
    rascunho,
    getRetificacaoStorageValue,
    idadeGestacional,
    cidadaoCpfCns,
    PILOTO_PARTICIPACAO_OBRIGATORIA_TELECONSULTA_ENABLED,
  ])

  useEffect(() => {
    if (shouldClearEncaminhamentosCache) {
      setCacheState((prevValue) => ({
        ...prevValue,
        plano: {
          ...prevValue.plano,
          encaminhamentoExterno: {
            encaminhamentosEspecializadosRecentes: [],
            encaminhamentosRecentes: [],
          },
        },
      }))
    }
  }, [shouldClearEncaminhamentosCache, setCacheState])

  const [problemasResolvidos, problemasAtivosELatentes] = useMemo(
    () => partition(problemas?.content, (problema) => problema.situacao === SituacaoProblema.RESOLVIDO),
    [problemas]
  )

  const isAnfitriaoVideochamada =
    !!agendado?.videochamada && agendado?.agendamentoPrincipal?.lotacaoAgendada?.id === acesso.id

  if (isLoading || !initialStateLoaded) {
    return (
      <>
        {tipoAtendimentoProfissional && <Breadcrumb title={`Atendimento ${nomeAtendimento}`} />}
        <PageLoading message='Carregando dados do atendimento individual...' />
      </>
    )
  }

  const renderFolhaRostoView = () => (
    <FolhaRostoView
      prontuarioId={prontuario.id}
      isGestante={isGestante}
      isPuerpera={isPuerpera}
      gestacoes={prontuario?.gestacoes || []}
      clearCache={deleteCacheState}
      isRegistroTardio={isRegistroTardio}
      dataReferencia={atendimentoProfissionalIniciadoEm}
      atendimentoId={atendimentoId}
      cidadao={cidadaoAtendimento}
      lotacao={lotacao}
      tipoAtendimentoProfissional={tipoAtendimentoProfissional}
      atendimentoProfissionalId={atendimentoProfissionalId}
      isAtendimentoAgendado={isAtendimentoAgendado}
      isAtendimentoObservacao={isAtendimentoObservacao}
      isObservacaoAndResponsavel={isObservacaoAndResponsavel}
      idadeGestacional={idadeGestacional}
      showVideochamada={isAnfitriaoVideochamada}
      isAtendimentoOdonto={isAtendimentoOdonto}
      isAtendimentoProcedimentos={showAsAtendimentoProcedimentos}
    />
  )

  const renderSoapView = () => (
    <SoapView
      atendimentoId={atendimentoId}
      prontuarioId={prontuarioId}
      cidadao={cidadaoAtendimento}
      dataAtendimento={atendimentoProfissionalIniciadoEm}
      procedimentosAutomaticos={procedimentosAutomaticos || []}
      cbo2002={cbo2002}
      agendamentoAtendimentoId={agendado?.id}
      concluiAgendamento={concluiAgendamento}
      hasEncaminhamentoExterno={hasEncaminhamentoExterno}
      tiposServico={tiposServico}
      agendamentosDia={agendamentosDia}
      atendimentoProfissional={atendimentoProfissional}
      isRegistroTardio={isRegistroTardio}
      registroAvaliacaoPuericultura={registroAvaliacaoPuericultura}
      nomeAtendimento={nomeAtendimento}
      ciapCidPreNatal={ciapCidPreNatal}
      problemasAtivosELatentes={problemasAtivosELatentes}
      problemasResolvidos={problemasResolvidos}
      gestacoes={prontuario?.gestacoes || []}
      cacheState={cacheState}
      dirtyInitialValues={valoresModificadosRascunhoRetificacao}
      updateCache={setCacheState}
      clearCache={handleDeleteCacheSoap}
      headerRef={headerRef}
      qtdTotalProcedimentos={qtdTotalProcedimentos}
      ultimaDumPreNatalAtivo={ultimaDumPreNatalAtivo}
      preNatalAtivoHasTipoGravidez={!isUndefinedOrNull(preNatalAtivo?.tipoGravidez)}
      hasIvcfPreenchidoEmAtendObservacaoAnterior={hasIvcfPreenchidoEmAtendObservacaoAnterior}
    />
  )

  const renderAtendimentoHistoricoView = () => (
    <HistoricoAtendimentoView
      atendimentoId={atendimentoId}
      prontuarioId={prontuario.id}
      cidadao={cidadao}
      unidadeSaudeCnes={unidadeSaude.cnes}
      tipoAtendimentoProfissional={tipoAtendimentoProfissional}
      isRegistroTardio={isRegistroTardio}
      clearCache={deleteCacheState}
      isAtendimentoObservacao={isAtendimentoObservacao}
      isObservacaoAndResponsavel={isObservacaoAndResponsavel}
    />
  )

  const renderCidadaoDetail = () => (
    <CidadaoDetailView
      atendimentoId={atendimentoId}
      cidadaoId={cidadao?.id}
      tipoAtendimentoProfissional={tipoAtendimentoProfissional}
      clearCache={deleteCacheState}
      isRegistroTardio={isRegistroTardio}
      callbackUrl={match.url}
      isAtendimentoObservacao={isAtendimentoObservacao}
      isObservacaoAndResponsavel={isObservacaoAndResponsavel}
    />
  )

  const renderVacinacao = () =>
    hasAccessToCadastrarLotesImunobiologico ? (
      <VacinacaoView
        atendimentoId={atendimentoId}
        cidadao={cidadaoAtendimento}
        gestante={isGestante}
        gestacoes={prontuario?.gestacoes || []}
        clearCache={deleteCacheState}
        dataAtendimento={atendimentoProfissionalIniciadoEm}
        prontuarioId={prontuario.id}
        agendamentosDia={agendamentosDia}
        cacheState={cacheState}
        updateCache={setCacheState}
        isAtendimentoVacinacao={false}
        tipoAtendimentoProfissional={tipoAtendimentoProfissional}
        isAtendimentoObservacao={isAtendimentoObservacao}
        isObservacaoAndResponsavel={isObservacaoAndResponsavel}
        isObservacaoAndAuxiliar={isObservacaoAndAuxiliar}
        tipoEstabelecimento={tipoEstabelecimento}
        atendimentoProfissionalId={atendimentoProfissionalId}
      />
    ) : (
      <VacinacaoReadOnlyView
        cidadao={{
          ...cidadaoAtendimento,
          racaCor: { id: undefined, nome: undefined, racaCorDbEnum: undefined },
          prontuario: prontuario,
        }}
        dataReferencia={Number(today)}
      />
    )

  const renderAcompanhamentos = () => (
    <AcompanhamentosView
      cidadaoId={cidadao?.id}
      prontuarioId={prontuario?.id}
      atendimentoId={atendimentoId}
      isGestante={isGestante}
      isPuerpera={acompanhamentoPuerperioAtivo}
      isVisualizacaoProntuario={false}
      isRegistroTardio={isRegistroTardio}
      dataReferencia={atendimentoProfissionalIniciadoEm}
      headerRef={headerRef}
      clearCache={deleteCacheState}
      isAtendimentoObservacao={isAtendimentoObservacao}
      isObservacaoAndResponsavel={isObservacaoAndResponsavel}
      tipoAtendimentoProfissional={tipoAtendimentoProfissional}
    />
  )

  const renderAgendamentos = () => (
    <AgendamentosCidadaoAtendimentoView
      atendimentoId={atendimentoId}
      tipoAtendimentoProfissional={tipoAtendimentoProfissional}
      clearCache={deleteCacheState}
      isRegistroTardio={isRegistroTardio}
      isAtendimentoObservacao={isAtendimentoObservacao}
      isObservacaoAndResponsavel={isObservacaoAndResponsavel}
    />
  )

  const renderSistemasExternos = () => (
    <SistemasExternosAtendimentoView
      atendimentoId={atendimentoId}
      cidadaoId={cidadao?.id}
      tipoAtendimentoProfissional={tipoAtendimentoProfissional}
      clearCache={deleteCacheState}
      isRegistroTardio={isRegistroTardio}
      isAtendimentoObservacao={isAtendimentoObservacao}
      isObservacaoAndResponsavel={isObservacaoAndResponsavel}
    />
  )

  const renderProntuarioFamiliar = () => <ProntuarioFamiliarView cidadaoId={cidadao?.id} />

  const canEdit = isObservacaoEmAndamento || isRetificacao
  const redirectTo = `${match.url}/${canEdit ? 'soap' : 'folha-rosto'}`

  const atendimentoProviderValue: AtendimentoContextInputModel = {
    ...atendimentoProfissional?.atendimento,
    cidadao: cidadaoAtendimento,
    idadeGestacional,
    atendimentoProfissional: atendimentoProfissional,
    observacao: {
      isAtendimentoObservacao,
      isObservacaoAndResponsavel,
      isObservacaoAndAuxiliar,
      isObservacaoEmAndamento,
      ultimoResponsavelObservacao,
      responsaveisObservacao,
      atendeuComoResponsavel,
    },
    prontuario: {
      ...prontuario,
      ...(isGestante && {
        preNatalAtivo: {
          ...prontuario.preNatalAtivo,
          altoRisco: isPreNatalAltoRisco,
        },
      }),
    },
    tipoEstabelecimento: tipoEstabelecimento,
    statusRevisaoAtendimento: statusRevisaoAtendimento,
    tiposAtendimento: {
      isAtendimentoProcedimentos: showAsAtendimentoProcedimentos,
      isAtendimentoOdonto,
      isAtendimentoAgendado,
    },
    isRetificacao,
  }

  return (
    <AtendimentoProvider input={atendimentoProviderValue}>
      {tipoAtendimentoProfissional && <Breadcrumb title={`Atendimento ${nomeAtendimento}`} />}
      <AtendimentoHeader
        ref={headerRef}
        cidadaoId={cidadao?.id}
        cnsProfissional={lotacao.profissional.cns}
        cnsCidadao={cidadao?.cns}
        cnes={lotacao.unidadeSaude.cnes}
        municipioId={lotacao.municipio.id}
        dataAtendimento={atendimentoProfissionalIniciadoEm}
        isRetificacao={isRetificacao}
        renderAcompanhamentos={hasToRenderAcompanhamentos}
        showAlertasSdc
        renderProntuarioFamiliar
        tipoAtendimentoProfissional={tipoAtendimentoProfissional}
      />
      <PecSwitch>
        <Redirect exact path={match.url} to={redirectTo} />
        <Route path={`${match.url}/soap`} render={renderSoapView} />
        {!isRetificacao && (
          <>
            <Route path={`${match.url}/folha-rosto`} render={renderFolhaRostoView} />
            <PrivateRoute
              path={`${match.url}/historico`}
              render={renderAtendimentoHistoricoView}
              permission={grupoCboHistorico.visualizar}
            />
            <Route path={`${match.url}/cadastro-cidadao`} render={renderCidadaoDetail} />
            <Route path={`${match.url}/vacinacao`} render={renderVacinacao} />
            {hasToRenderAcompanhamentos && (
              <PrivateRoute
                path={`${match.url}/${ACOMPANHAMENTOS_URL}`}
                render={renderAcompanhamentos}
                permission={Permissions.visualizarProntuario}
                cbos={grupoCboVisualizarAcompanhamentos}
              />
            )}
            <Route path={`${match.path}/agendamentos/:cidadaoId`} render={renderAgendamentos} />
            <Route path={`${match.path}/sistemas-externos/:iframeId?`} render={renderSistemasExternos} />
            {PILOTO_PRONTUARIO_FAMILIAR_ENABLED && (
              <PrivateRoute
                path={`${match.path}/prontuario-familiar`}
                render={renderProntuarioFamiliar}
                permission={Permissions.gestaoDeCadastros.visualizarCadastroIndividual}
              />
            )}
          </>
        )}
      </PecSwitch>
      {agendado?.videochamada && agendado.agendamentoPrincipal && !isAnfitriaoVideochamada && (
        <ConfirmarNaoAnfitriaoAtenderModal
          atendimentoId={atendimentoId}
          clearCache={deleteCacheState}
          nomeProfissionalAnfitriao={agendado.agendamentoPrincipal.lotacaoAgendada.profissional.nome}
        />
      )}
      {/* TODO (RNG)
       * Necessário ajustar o botão "Finalizar atendimento individual",
       * para utilizar o componente aqui.
       * Enquanto isso, é colocado em cada aba.
       * <AtendimentoFooter atendimentoId={atendimentoId} clearCache={deleteCacheState} />
       * */}
    </AtendimentoProvider>
  )
}

// copiado do bold pra memoizar o retorno, mas deve ser corrigido lá depois
export type StyleFactory<Classes extends string> = (
  theme: Theme,
  ...args: any[]
) => { readonly [key in Classes]: React.CSSProperties }

export type ClassNames<Classes extends string> = { readonly [key in Classes]: string }

const emptyStylesFactory: StyleFactory<any> = () => ({})

export const useLocalStyles = <Classes extends string>(
  factory: StyleFactory<Classes> = emptyStylesFactory,
  ...args: any[]
) => {
  const { css, theme } = useCss()

  return useMemo(() => {
    const map = factory(theme, ...args)

    const classes = Object.keys(map).reduce(
      (all, className) => ({
        ...all,
        [className]: css(map[className]),
      }),
      {} as ClassNames<Classes>
    )

    return { classes, css, theme }
  }, [args, css, factory, theme])
}

const cacheParser = (cacheState: SoapState): SoapState =>
  cacheState
    ? {
        ...cacheState,
        finalizacao: {
          ...cacheState.finalizacao,
          agendamentoConsultas: {
            ...cacheState.finalizacao?.agendamentoConsultas,
            avulsa: { ...parseHorarioAgendamento(cacheState.finalizacao?.agendamentoConsultas?.avulsa) },
            proximasConsultas: {
              proximaConsultaPreNatal: {
                ...parseHorarioAgendamento(
                  cacheState.finalizacao?.agendamentoConsultas?.proximasConsultas?.proximaConsultaPreNatal
                ),
              },
              proximaConsultaOdontologica: {
                ...parseHorarioAgendamento(
                  cacheState.finalizacao?.agendamentoConsultas?.proximasConsultas?.proximaConsultaOdontologica
                ),
              },
            },
          },
        },
      }
    : undefined

const parseHorarioAgendamento = (agendamento: AgendarConsultaSectionModel) =>
  agendamento
    ? {
        ...agendamento,
        horario: parseDateFromLocalStorage(agendamento.horario),
      }
    : undefined

const filterTiposServico = (
  tiposServico: ReadonlyArray<TipoServicoModel>,
  tipoAtendProf: TipoAtendimentoProfissional
): TipoServicoModel[] =>
  tiposServico?.filter((item) => !tipoAtendimentoToTipoServicoMap[tipoAtendProf]?.includes(item.id))

function getTipoAtendimentoInitialValue(
  tipoEstabelecimento: TipoEstabelecimentoEnum,
  isObservacaoAndResponsavel: boolean
) {
  const isUpa = tipoEstabelecimento === TipoEstabelecimentoEnum.UPA
  return isUpa && isObservacaoAndResponsavel ? TipoAtendimentoEnum.URGENCIA : undefined
}
